import React, { HTMLAttributes, InputHTMLAttributes, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ImageUploaderConfig } from './useImageUploader.typed';
import { accessibleOnClick } from '@dist-marketplace-frontend/utils';

// TODO: File compression or should that be managed outside? maybe default compression function and can pass a custom one
const DEFAULT_ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png'];

/**
 * A utility function that accepts multiple functions and calls them all with the same arguments.
 */
const callAll =
  <Args extends unknown[]>(
    ...fns: Array<((...args: Args) => void) | undefined>
  ) =>
  (...args: Args) => {
    fns.forEach((fn) => fn && fn?.(...args));
  };
/**
 * A hook to manage the state of an image uploader
 * @param config
 */
export const useImageUploader = (config: ImageUploaderConfig) => {
  const {
    acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
    onUpload,
    onReady,
    onInit,
    onError,
    count,
    maxCount,
  } = config;
  /**
   * A ref to the input element
   */
  const inputRef = useRef<HTMLInputElement>(null);
  const fileName = inputRef.current?.name || 'file';

  // Helpers
  const getFormData = (file: File) => {
    const formData = new FormData();
    formData.append(fileName, file);
    return formData;
  };
  const getFilePreview = (file: File) => {
    return URL.createObjectURL(file);
  };
  const isValidFile = (file: File) => {
    return acceptedFileTypes.includes(file.type);
  };
  const capFiles = (files: File[]): File[] => {
    if (!!maxCount && count + files.length > maxCount) {
      const filesToRemove = count + files.length - maxCount;
      if (filesToRemove) {
        files.splice(-filesToRemove);
      }
    }
    return files;
  };
  const onFilesAdded = (files: FileList) => {
    const filesArray = maxCount
      ? capFiles(Array.from(files))
      : Array.from(files);
    for (const file of filesArray) {
      processFile(file);
    }
  };
  const processFile = (file: File) => {
    const id = uuidv4();
    onInit(id);
    if (!isValidFile(file)) {
      onError(id, new Error('Invalid file type'));
      return;
    }
    onReady(id, getFilePreview(file));
    onUpload(id, getFormData(file));
  };

  // Events
  /**
   * An event to capture the file input click event
   * @param event
   */
  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    // Prevent event bubbling up to prevent double onClicks if nested elements with onClick event
    event.stopPropagation();
    inputRef?.current?.click();
  };
  /**
   * An event to capture the file input change event
   * @param event
   */
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    onFilesAdded(event.target.files);
  };
  /**
   * An event to capture the drag over event necessary for drag and drop of files
   * Without using this function onDrop will not work
   * @param event
   */
  const onDragOver = (event: React.DragEvent<HTMLElement>) => {
    // Preventing defaults tells the browser to allow dropping of the file
    // Stop propagation is good if you want to use nested drop points.
    event.preventDefault();
    event.stopPropagation();
  };
  /**
   * An event to capture the drop event necessary for drag and drop of files
   * @param event
   */
  const onDrop = (event: React.DragEvent<HTMLElement>) => {
    // preventing default stops the browser from trying to open the file in a new tab
    event.preventDefault();
    event.stopPropagation();
    if (!event.dataTransfer.files) return;
    onFilesAdded(event.dataTransfer.files);
  };

  return {
    getInputProps: (props: InputHTMLAttributes<HTMLInputElement> = {}) => ({
      ref: inputRef,
      type: 'file',
      accept: acceptedFileTypes.join(','),
      ...props,
      onChange: callAll(props.onChange, onChange),
    }),
    getTargetProps: (
      props: { accessible?: boolean } & HTMLAttributes<HTMLElement> = {
        accessible: false,
      },
    ) => {
      const { accessible, ...rest } = props;
      return {
        ...rest,
        ...(accessible
          ? accessibleOnClick(callAll(props.onClick, onClick))
          : {
              onClick: callAll(props.onClick, onClick),
            }),
        onDrop: callAll(props.onDrop, onDrop),
        onDragOver: callAll(props.onDragOver, onDragOver),
      };
    },
  };
};
